import { useCallback, useEffect, useRef } from 'react';
import useDetectRateMode from 'components/Dashboard/ListControl/useDetectRateMode';
import {
  getActiveUpdateAtIndex,
  getFirstUnratedIndex,
  loadNextRateUpdatesPage,
  scrollToRateUpdateAtIndex,
  shouldUpdateActiveItem,
} from 'components/Dashboard/RateUpdatesFeed/rateUpdatesHelpers';

const useRateUpdatesListFocus = ({
  sectionName,
  section,
  isLoading,
  createResults,
  scrollBarRef,
  activeUpdate,
  setActiveUpdate,
  registerLoadNextRateUpdatesPage,
}) => {
  const { rateUpdatesMode } = useDetectRateMode();

  const focusGenerationRef = useRef(0);
  const fetchingRef = useRef(false);
  const prevPageNumberRef = useRef(null);
  const pendingScrollRef = useRef(false);
  const pendingSectionFocusRef = useRef(null);

  const createResultsRef = useRef(createResults);
  createResultsRef.current = createResults;
  const sectionRef = useRef(section);
  sectionRef.current = section;

  const loadNextRateUpdatesPageForSection = useCallback(async () => {
    return loadNextRateUpdatesPage({ createResults: createResultsRef.current, section: sectionRef.current });
  }, []);

  const registerLoadNextRateUpdatesPageRef = useRef(registerLoadNextRateUpdatesPage);
  registerLoadNextRateUpdatesPageRef.current = registerLoadNextRateUpdatesPage;

  useEffect(() => {
    if (!rateUpdatesMode || isLoading) {
      return;
    }
    focusGenerationRef.current += 1;
    prevPageNumberRef.current = null;
    pendingScrollRef.current = true;
    pendingSectionFocusRef.current = sectionName;
    scrollBarRef.current?.scrollToTop?.();
  }, [sectionName, rateUpdatesMode, isLoading, scrollBarRef]);

  useEffect(() => {
    registerLoadNextRateUpdatesPageRef.current?.(loadNextRateUpdatesPageForSection);
  }, [loadNextRateUpdatesPageForSection]);

  useEffect(() => {
    if (!rateUpdatesMode || isLoading || !setActiveUpdate) {
      return;
    }
    if (pendingSectionFocusRef.current !== sectionName) {
      return;
    }

    const feedItemFlags = section?.feedItemFlags;
    const hits = section?.hits;
    if (!section?.loaded || !hits?.length || !feedItemFlags?.length) {
      return;
    }

    const index = getFirstUnratedIndex(feedItemFlags);
    if (index === -1) {
      return;
    }

    const focusGeneration = focusGenerationRef.current;
    const nextUpdate = getActiveUpdateAtIndex({ hits, feedItemFlags, index });
    if (shouldUpdateActiveItem(activeUpdate, nextUpdate)) {
      setActiveUpdate(nextUpdate);
    }

    pendingSectionFocusRef.current = null;
    pendingScrollRef.current = false;
    scrollToRateUpdateAtIndex(scrollBarRef, index, {
      delay: 400,
      isStale: () => focusGeneration !== focusGenerationRef.current,
    });
  }, [rateUpdatesMode, section?.feedItemFlags?.length]);

  useEffect(() => {
    const focusGeneration = focusGenerationRef.current;
    if (!rateUpdatesMode || isLoading || !setActiveUpdate) {
      return;
    }

    const feedItemFlags = section?.feedItemFlags;
    const hits = section?.hits;
    if (!section?.loaded || !hits?.length) {
      return;
    }

    const currentPageNumber = section?.pageNumber ?? 1;
    const pageIncreased = prevPageNumberRef.current !== null && currentPageNumber > prevPageNumberRef.current;
    prevPageNumberRef.current = currentPageNumber;

    const index = getFirstUnratedIndex(feedItemFlags ?? []);
    if (index !== -1) {
      fetchingRef.current = false;
      const nextUpdate = getActiveUpdateAtIndex({ hits, feedItemFlags, index });
      const activeChanged = shouldUpdateActiveItem(activeUpdate, nextUpdate);
      if (activeChanged) {
        setActiveUpdate(nextUpdate);
      }
      const shouldScroll = activeChanged || pageIncreased || pendingScrollRef.current;
      if (shouldScroll) {
        pendingScrollRef.current = false;
        pendingSectionFocusRef.current = null;
        scrollToRateUpdateAtIndex(scrollBarRef, index, {
          delay: pageIncreased ? 300 : 250,
          isStale: () => focusGeneration !== focusGenerationRef.current,
        });
      }
      return;
    }

    if (section?.thereIsMoreContent && !fetchingRef.current) {
      fetchingRef.current = true;
      pendingScrollRef.current = true;
      loadNextRateUpdatesPageForSection().finally(() => {
        fetchingRef.current = false;
      });
      return;
    }

    const fallbackUpdate = getActiveUpdateAtIndex({ hits, feedItemFlags, index: 0 });
    if (shouldUpdateActiveItem(activeUpdate, fallbackUpdate)) {
      setActiveUpdate(fallbackUpdate);
    }
  }, [rateUpdatesMode, isLoading]);

  return { rateUpdatesMode, loadNextRateUpdatesPageForSection };
};

export default useRateUpdatesListFocus;
